import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Shield, Users, Leaf } from "lucide-react";
import camionnettesAgro from "@/assets/camionnettes-agro-new.jpg";
import ScrollReveal from "@/components/ScrollReveal";
import customerCareIcon from "@/assets/customer-care-icon.png";

const APropos = () => {
  return (
    <>
      <Helmet>
        <title>À Propos - Agro-Réfrigération</title>
        <meta 
          name="description" 
          content="Découvrez l'histoire d'Agro-Réfrigération, fondée en 1988 par Normand Tremblay et Réjean Houle." 
        />
        <link rel="preload" as="image" href={camionnettesAgro} />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero Section */}
        <section className="bg-[#2c2c2c] text-white py-12 md:py-16 md:py-20 relative">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10 md:mb-12">
              <h1 className="text-3xl md:text-5xl font-bold mb-4">Découvrez Agro</h1>
              <div className="w-32 md:w-48 h-0.5 bg-accent mx-auto mb-6"></div>
              <p className="text-base md:text-lg text-white">
                Votre spécialiste en équipements robotisés et traite laitière
              </p>
            </div>
            
            <div className="flex justify-center">
              <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-2xl inline-block">
                <img 
                  src={camionnettesAgro}
                  alt="Camionnettes Agro"
                  className="block max-w-full h-auto max-h-[400px] md:max-h-[500px]"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
          
          {/* Courbe blanche en bas */}
          <div className="absolute bottom-0 left-0 right-0 -mb-px">
            <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" preserveAspectRatio="none">
              <path d="M0 0C240 53.3333 480 80 720 80C960 80 1200 53.3333 1440 0V80H0V0Z" fill="white"/>
            </svg>
          </div>
        </section>

        {/* Notre Histoire et Mission */}
        <section className="bg-white py-12 md:py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto">
              {/* Notre Histoire */}
              <ScrollReveal direction="left" blur>
                <h2 className="text-2xl font-bold mb-6 text-foreground">Notre histoire:</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Agro a été fondée en octobre 1988 par Messieurs Normand Tremblay et Réjean 
                    Houle, deux entrepreneurs passionnés par l'agriculture et l'industrie laitière. Au 
                    fil des ans, l'entreprise a grandi pour devenir un des fournisseurs clés de solutions 
                    pour les fermes laitières dans la région du Centre-du-Québec, de l'Estrie et de Lotbinière.
                  </p>
                  <p>
                    À ses débuts, l'activité d'Agro se focalisait essentiellement sur la réparation 
                    d'équipements. L'entreprise a élargi sa gamme de produits en 1993 en intégrant les 
                    équipements Boumatic. En 2008, une étape significative a été franchie avec 
                    l'introduction des produits Lely, reconnu comme leader mondial en automatisation. 
                    Cette expansion a renforcé leur gamme en y ajoutant des solutions innovantes pour 
                    la traite du lait et le stockage.
                  </p>
                </div>
              </ScrollReveal>

              {/* Mission et Vision */}
              <ScrollReveal direction="right" delay={0.2} blur>
                <h2 className="text-2xl font-bold mb-6 text-foreground">Mission et vision:</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Agro a une mission d'offrir leurs solutions complètes dans sa région du Centre-
                    du-Québec, de l'Estrie et de Lotbinière; le tout pour maintenir et atteindre une 
                    excellence et efficacité des fermes laitières de façon à livrer la qualité de lait nécessaire 
                    à tous ses besoins et particularités et en assumant en service à la clientèle exceptionnelle.
                  </p>
                  <p>
                    Notre vision est d'être un acteur dans l'industrie laitière, reconnue de manière 
                    positive pour sa capacité d'offrir des équipements et des services innovants. Notre 
                    ambition fait avancer et assure notre présence avec éthique et durabilité vers ses clients.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Nos Valeurs */}
        <section className="bg-gray-50 py-12 md:py-16">
          <div className="container mx-auto px-4">
            <ScrollReveal>
              <h2 className="text-3xl font-bold text-center mb-8 md:mb-12">Nos valeurs:</h2>
            </ScrollReveal>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {/* Innovation */}
              <ScrollReveal delay={0.1}>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-start gap-3 mb-4">
                    <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <h3 className="font-bold text-lg">Innovation</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Chez Agro, nous sommes constamment engagés dans la recherche 
                    et l'adoption de technologies de pointe et de solutions innovantes. Nous cherchons 
                    toujours moyens d'innover dans toutes les relations et contrats faisant en 
                    sorte d'influencer de leur production laitière grâce à nos solutions toujours à l'écoute.
                  </p>
                </div>
              </ScrollReveal>

              {/* Intégrité */}
              <ScrollReveal delay={0.2}>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-start gap-3 mb-4">
                    <Shield className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <h3 className="font-bold text-lg">Intégrité</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Nous sommes honorées et l'expérience de l'intégrité-chef en 
                    s'efforçant avec fermeté dans l'information. Nos clients peuvent toujours 
                    compter sur nous pour des avis francs avec respect et estime dans tous nos 
                    échanges et leurs partenariats avec satisfaction.
                  </p>
                </div>
              </ScrollReveal>

              {/* Satisfaction du service */}
              <ScrollReveal delay={0.3}>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-start gap-3 mb-4">
                    <Users className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <h3 className="font-bold text-lg">Satisfaction du service</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Nous déployons un effort de façon structurellement inclusive en ville 
                    toutes les temps principaux et nous sommes d'AGR au fil, 
                    toujours à leurs services adaptés et moins sous (AGR) et 
                    dévoués et bien pour assurer une prestation et la meilleure disposition.
                  </p>
                </div>
              </ScrollReveal>

              {/* Durabilité */}
              <ScrollReveal delay={0.4}>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-start gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <h3 className="font-bold text-lg">Durabilité</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Nous pratique économique dans la respect environnementalement de fermeture 
                    durables. Nos objectifs - la main d'aides équilibres en poids 
                    pratiques compétitives avec leur évolution également part l'économiques avec 
                    biens écologiquement continue leur équilibre - une cause de société.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative bg-[#333333] text-white py-12 md:py-16 overflow-hidden">
          <img src={customerCareIcon} alt="" className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 object-contain pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <ScrollReveal>
              <p className="text-sm uppercase tracking-wider mb-4">CONTACTEZ-NOUS</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Prêt à Optimiser Votre Production Laitière?
              </h2>
              <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
                Exploitez pleinement le potentiel de votre production laitière avec Agro. Contactez-nous dès aujourd&apos;hui pour découvrir comment
              </p>
              <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
                nos services et produits peuvent aider à améliorer l&apos;efficacité de votre exploitation et garantir la meilleure qualité de lait possible.
              </p>
              <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
                Nous sommes disponibles 24/7 pour vous assister dans vos urgences.
              </p>
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
                <Link to="/contact">Contactez-nous dès maintenant!</Link>
              </Button>
            </ScrollReveal>
          </div>
        </section>
      </div>
    </>
  );
};

export default APropos;
